/**
 * LeadsInboxPage — super-admin inbox of landing-page inquiries.
 *
 * Replaces the "read leads via the Supabase dashboard" workflow noted in
 * 20260508000004_leads.sql with an in-app surface. Data comes from the
 * report-leads Edge Function (service_role read, super_admin gated) via
 * useLeads. The page is also gated by RequireRole(['super_admin']) in the
 * router, so this component only renders for the platform operator.
 *
 * Triage affordances: a vertical filter (client-side) and most-recent-first
 * ordering (done server-side). Each row shows the inquiry type, contact,
 * sizing inputs, and the AI fit grade when present.
 */

import { useState, useMemo, type ReactNode } from 'react'
import { useTranslation }                     from 'react-i18next'
import {
  useLeads,
  type LeadRow,
  type LeadVertical,
  type LeadInquiryType,
} from '@/hooks/useLeads'

type VerticalFilter = LeadVertical | 'all'

const VERTICAL_LABEL: Record<LeadVertical, string> = {
  dorm:          'Dorm',
  office:        'Office',
  property_mgmt: 'Property mgmt',
}

const INQUIRY_LABEL: Record<LeadInquiryType, string> = {
  info:     'Info',
  quote:    'Quote',
  demo:     'Demo',
  waitlist: 'Waitlist',
}

export function LeadsInboxPage(): ReactNode {
  const { t } = useTranslation()
  const { data, isLoading, error } = useLeads()
  const [vertical, setVertical] = useState<VerticalFilter>('all')

  const leads = useMemo(() => {
    const list = data ?? []
    return vertical === 'all' ? list : list.filter((l) => l.vertical === vertical)
  }, [data, vertical])

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <header>
        <h1 style={{ margin: 0, fontSize: '1.4rem', fontFamily: 'var(--brand-font-family-display)' }}>
          {t('leads.title', 'Leads')}
        </h1>
        <p style={{ margin: '0.25rem 0 0', color: 'var(--brand-neutral-500)' }}>
          {t('leads.subtitle', 'Inquiries captured from the marketing landing page.')}
        </p>
      </header>

      {isLoading && <Hint>{t('leads.loading', 'Loading leads…')}</Hint>}
      {error && (
        <ErrorPanel
          message={error instanceof Error ? error.message : t('leads.error', 'Could not load leads.')}
        />
      )}

      {!isLoading && !error && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <label htmlFor="leads-vertical-filter" style={{ fontSize: '0.85rem', color: 'var(--brand-neutral-700)' }}>
            {t('leads.filterVertical', 'Vertical')}
          </label>
          <select
            id="leads-vertical-filter"
            data-testid="leads-vertical-filter"
            value={vertical}
            onChange={(e) => setVertical(e.target.value as VerticalFilter)}
            style={{
              padding:      '0.35rem 0.5rem',
              border:       '1px solid var(--brand-neutral-300)',
              borderRadius: 'var(--brand-radius-sm)',
              fontSize:     '0.85rem',
              background:   'var(--brand-white)',
            }}
          >
            <option value="all">{t('leads.allVerticals', 'All verticals')}</option>
            <option value="dorm">{VERTICAL_LABEL.dorm}</option>
            <option value="office">{VERTICAL_LABEL.office}</option>
            <option value="property_mgmt">{VERTICAL_LABEL.property_mgmt}</option>
          </select>
          <span style={{ marginLeft: 'auto', fontSize: '0.8rem', color: 'var(--brand-neutral-500)' }}>
            {t('leads.count', '{{count}} shown', { count: leads.length })}
          </span>
        </div>
      )}

      {!isLoading && !error && leads.length === 0 && (
        <Hint>{t('leads.empty', 'No leads in this window.')}</Hint>
      )}

      {!isLoading && !error && leads.length > 0 && (
        <ul
          data-testid="leads-list"
          style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '0.6rem' }}
        >
          {leads.map((lead) => <LeadCard key={lead.id} lead={lead} />)}
        </ul>
      )}
    </section>
  )
}

// ─────────────────────────────────────────────────────────────────────

function LeadCard({ lead }: { lead: LeadRow }): ReactNode {
  const { t } = useTranslation()
  const sizing: string[] = []
  if (lead.rooms_count !== null)      sizing.push(t('leads.rooms', '{{count}} rooms', { count: lead.rooms_count }))
  if (lead.properties_count !== null) sizing.push(t('leads.properties', '{{count}} properties', { count: lead.properties_count }))

  return (
    <li
      data-testid="lead-row"
      data-vertical={lead.vertical}
      style={{
        background:    'var(--brand-white)',
        border:        '1px solid var(--brand-neutral-300)',
        borderRadius:  'var(--brand-radius-md)',
        padding:       '0.75rem 1rem',
        display:       'flex',
        flexDirection: 'column',
        gap:           '0.4rem',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
        <Badge tone="primary">{VERTICAL_LABEL[lead.vertical]}</Badge>
        <Badge tone="neutral">{INQUIRY_LABEL[lead.inquiry_type]}</Badge>
        <FitBadge lead={lead} />
        <span style={{ marginLeft: 'auto', color: 'var(--brand-neutral-500)', fontSize: '0.8rem' }}>
          {new Date(lead.created_at).toLocaleString()}
        </span>
      </div>

      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'baseline' }}>
        <a href={`mailto:${lead.email}`} style={{ color: 'var(--brand-primary)', fontWeight: 600, fontSize: '0.95rem' }}>
          {lead.email}
        </a>
        {(lead.name || lead.company) && (
          <span style={{ color: 'var(--brand-neutral-700)', fontSize: '0.9rem' }}>
            {[lead.name, lead.company].filter(Boolean).join(' · ')}
          </span>
        )}
      </div>

      {sizing.length > 0 && (
        <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--brand-neutral-700)' }}>{sizing.join(' · ')}</p>
      )}

      {lead.message && (
        <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--brand-neutral-900)' }}>{lead.message}</p>
      )}

      {(lead.utm_source || lead.utm_campaign) && (
        <p style={{ margin: 0, fontSize: '0.75rem', color: 'var(--brand-neutral-500)' }}>
          {[lead.utm_source, lead.utm_medium, lead.utm_campaign].filter(Boolean).join(' / ')}
        </p>
      )}
    </li>
  )
}

function FitBadge({ lead }: { lead: LeadRow }): ReactNode {
  const { t } = useTranslation()
  if (lead.grade_status === 'graded' && lead.fit_score !== null) {
    const tone = lead.fit_score >= 67 ? 'success' : lead.fit_score >= 34 ? 'primary' : 'neutral'
    return (
      <Badge tone={tone} title={lead.fit_reason ?? undefined}>
        {t('leads.fitScore', 'Fit {{score}}', { score: lead.fit_score })}
      </Badge>
    )
  }
  if (lead.grade_status === 'unavailable' || lead.grade_status === 'malformed') {
    return <Badge tone="neutral">{t('leads.fitUngraded', 'Ungraded')}</Badge>
  }
  return null
}

type Tone = 'primary' | 'neutral' | 'success'

function Badge({ tone, title, children }: { tone: Tone; title?: string; children: ReactNode }): ReactNode {
  const palette: Record<Tone, { bg: string; fg: string }> = {
    primary: { bg: 'var(--brand-primary-subtle)', fg: 'var(--brand-primary)' },
    neutral: { bg: 'var(--brand-neutral-100)',    fg: 'var(--brand-neutral-700)' },
    success: { bg: 'var(--brand-success-subtle)', fg: 'var(--brand-success)' },
  }
  const c = palette[tone]
  return (
    <span
      title={title}
      style={{
        fontSize:     '0.7rem',
        padding:      '0.1rem 0.45rem',
        borderRadius: 'var(--brand-radius-full)',
        background:   c.bg,
        color:        c.fg,
        fontWeight:   600,
      }}
    >
      {children}
    </span>
  )
}

function Hint({ children }: { children: ReactNode }): ReactNode {
  return <p style={{ color: 'var(--brand-neutral-500)' }}>{children}</p>
}

function ErrorPanel({ message }: { message: string }): ReactNode {
  return (
    <div
      role="alert"
      style={{
        background:   'var(--brand-danger-subtle)',
        color:        'var(--brand-danger)',
        border:       '1px solid var(--brand-danger)',
        borderRadius: 'var(--brand-radius-md)',
        padding:      '0.5rem 0.75rem',
        fontSize:     '0.85rem',
      }}
    >
      {message}
    </div>
  )
}
